import "./instrument";

import { StrictMode, lazy } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import * as Sentry from "@sentry/react";
import ErrorBoundary from "./components/ErrorBoundary.tsx";
import RootLayout from "./components/RootLayout.tsx";
import TabLayout from "./components/TabLayout.tsx";

import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";
import { Toaster } from "sonner";
import { REFETCH_INTERVAL_MS } from "./lib/query";

// Lazy load tab components
const FavoritesTab = lazy(() => import("./features/stops/favorites-tab.tsx"));
const FavoritesArrivalsPage = lazy(
  () => import("./features/stops/pages/favorites-arrivals-page.tsx"),
);
// Full-screen first-run brand takeover — rendered outside TabLayout (no tab bar).
const LocationLandingPage = lazy(
  () => import("./features/location-landing/location-landing-page.tsx"),
);
// The /nearby index redirect gate (routes for returning users, landing otherwise).
const NearbyIndexRedirect = lazy(
  () => import("./features/nearby/pages/nearby-index-redirect.tsx"),
);
const NearbyLayout = lazy(() => import("./features/nearby/nearby-layout.tsx"));
const NearbyRoutesPage = lazy(
  () => import("./features/nearby/pages/nearby-routes-page.tsx"),
);
const NearbyStopsPage = lazy(
  () => import("./features/nearby/pages/nearby-stops-page.tsx"),
);
const NearbyArrivalsPage = lazy(
  () => import("./features/nearby/pages/nearby-arrivals-page.tsx"),
);
// Placeholder Explore destination for the Escape Hatch until spike #107 lands.
const ExplorePlaceholderPage = lazy(
  () => import("./features/explore/explore-placeholder-page.tsx"),
);
// const NearbyProgressPage = lazy(
//   () => import("./features/nearby/pages/nearby-progress-page.tsx"),
// );

// const RoutesTab = lazy(() => import("./features/routes/routes-tab.tsx"));
const TrackLayout = lazy(() => import("./features/track/track-layout.tsx"));
const TrackLandingPage = lazy(
  () => import("./features/track/pages/track-landing-page.tsx"),
);
const TrackProgressPage = lazy(
  () => import("./features/track/pages/track-progress-page.tsx"),
);

posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string, {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string,
  person_profiles: "identified_only",
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      gcTime: 300000,
      refetchInterval: REFETCH_INTERVAL_MS,
      refetchOnWindowFocus: true,
      retry: 2,
    },
  },
});

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    // RootLayout owns the shared location-permission context for both surfaces
    // below: the full-screen landing and the tab app.
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      // First-run brand takeover at the front door — deliberately NOT wrapped in
      // TabLayout, so it is full-screen with no tab bar.
      { index: true, element: <LocationLandingPage /> },
      // The tab app. Pathless layout route: wraps every tab in the tab shell
      // (status bar + bottom nav) without adding a URL segment.
      {
        element: <TabLayout />,
        children: [
          // Placeholder Explore tab (spike #107). The Escape Hatch navigates here
          // via EXPLORE_PATH; repoint that constant + this route when #107 lands.
          { path: "explore", element: <ExplorePlaceholderPage /> },
          {
            path: "favorites",
            children: [
              { index: true, element: <FavoritesTab /> },
              { path: ":id/arrivals", element: <FavoritesArrivalsPage /> },
            ],
          },
          {
            path: "nearby",
            element: <NearbyLayout />,
            children: [
              { index: true, element: <NearbyIndexRedirect /> },
              { path: "routes", element: <NearbyRoutesPage /> },
              { path: "routes/:routeId/stops", element: <NearbyStopsPage /> },
              {
                path: "routes/:routeId/stops/:stopId/arrivals",
                element: <NearbyArrivalsPage />,
              },
              // TODO: Add progress/map view for a tracked trip
              // Blocked on: /api/shapes endpoint returning route polyline geometry
              // Once available: build a map component and wire NearbyProgressPage
              // at path: "routes/:routeId/stops/:stopId/arrivals/:tripId/progress"
            ],
          },
          // { path: "routes", element: <RoutesTab /> },
          {
            path: "track",
            element: <TrackLayout />,
            children: [
              { index: true, element: <TrackLandingPage /> },
              { path: "trip/:tripId", element: <TrackProgressPage /> },
            ],
          },
        ],
      },
    ],
  },
]);

async function prepare() {
  if (import.meta.env.VITE_MSW_ENABLED === "true") {
    const { worker } = await import("./mocks/browser");
    await worker.start({ onUnhandledRequest: "bypass" });
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

await prepare();

createRoot(rootElement,
   {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}
).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </PostHogProvider>
  </StrictMode>,
);
